import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { Permission } from '../../../../models/core/departments.type';
import { DestinationType } from '../../../../models/core/destinations.type';
import destinationsDestination from '../../../../services/core/destinations.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: DestinationType | null;
};

function DestinationView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idResiduoDestinoFinal } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.destination === undefined,
    data: location?.state?.destination || null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: DestinationType | null = null;
      try {
        data = await destinationsDestination.select({
          idResiduoDestinoFinal: Number(idResiduoDestinoFinal),
        });
      } catch (error: any) {
        showNotification({
          title: 'Destino Final - Visualizar',
          message: error?.isBusinessException
            ? error.description
            : `Não foi possível carregar o destino final.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idResiduoDestinoFinal, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await destinationsDestination.delete({ idResiduoDestinoFinal: Number(idResiduoDestinoFinal) });
      navigate('/destinations');

      showNotification({
        title: 'Destino Final - Visualizar',
        message: `Destino final excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Destino Final - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir o destino final.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse destino final?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Reference.Destination}
        title="Destino Final - Visualizar"
        description="Visualize os detalhes de um destino final."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            loading={deleting}
            data-permission={Permission.DestinationDelete}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/destinations/${result?.data?.idResiduoDestinoFinal}/edit`, {
                state: {
                  destination: result.data,
                },
              });
            }}
            disabled={deleting || result.loading}
            data-permission={Permission.DestinationEdit}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default DestinationView;
